import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useI18n } from '../I18nContext';
import LanguageSwitcher from './LanguageSwitcher';
import { cn } from '../lib/utils';
import { Menu, X, Home, Map, Info, TreePine, User, Shield, LogOut, Activity, FileText, Leaf, Printer, ClipboardCheck } from 'lucide-react';

function Layout({ children }) {
  const location = useLocation();
  const { farmer, logout } = useAuth();
  const { t } = useI18n();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const isActive = (path) => location.pathname === path;

  const navLinkClass = (active) => cn(
    'inline-flex items-center px-3.5 py-1.5 rounded-lg text-sm font-semibold text-white/90 no-underline transition-all min-h-9',
    active ? 'bg-white/18 opacity-100' : 'hover:bg-white/10 opacity-80 hover:opacity-100'
  );

  const drawerLinkClass = (active) => cn(
    'flex items-center gap-2 px-4 py-3 rounded-xl text-base font-semibold text-white no-underline transition-all min-h-12',
    active ? 'bg-white/16' : 'hover:bg-white/10'
  );

  const navLinks = (
    <>
      <Link to="/" className={navLinkClass(isActive('/'))}>{t('nav.home')}</Link>
      <Link to="/dashboard" className={navLinkClass(isActive('/dashboard'))}>{t('nav.dashboard')}</Link>
      <Link to="/about" className={navLinkClass(isActive('/about'))}>{t('nav.about')}</Link>
      {farmer ? (
        <>
          <Link to="/my-trees" className={navLinkClass(isActive('/my-trees'))}>{t('nav.myTrees')}</Link>
          <Link to="/ndvi" className={navLinkClass(isActive('/ndvi'))}>{t('nav.ndvi')}</Link>
          <Link to="/eudr-compliance" className={navLinkClass(isActive('/eudr-compliance'))}>{t('nav.eudr')}</Link>
          <Link to="/carbon-farming" className={navLinkClass(isActive('/carbon-farming'))}>{t('nav.crcf')}</Link>
          <Link to="/mrv" className={navLinkClass(isActive('/mrv'))}>MRV</Link>
          <Link to="/tree-labels" className={navLinkClass(isActive('/tree-labels'))}>{t('nav.labels')}</Link>
          <Link to="/profile" className={navLinkClass(isActive('/profile'))}>{t('nav.profile')}</Link>
          {farmer.is_admin && (
            <>
              <Link to="/admin" className={navLinkClass(isActive('/admin'))}>{t('nav.admin')}</Link>
              <Link to="/field-validation" className={navLinkClass(isActive('/field-validation'))}>Field</Link>
            </>
          )}
          <div className="w-px h-5 bg-white/20 mx-1" />
          <span className="inline-flex items-center px-2.5 py-2 text-sm text-white/75">{farmer.name}</span>
          <button onClick={logout} className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-semibold text-white/85 bg-white/10 border-none cursor-pointer transition-all hover:bg-white/20">
            <LogOut size={15} /> {t('nav.logout')}
          </button>
        </>
      ) : (
        <>
          <Link to="/login" className={navLinkClass(isActive('/login'))}>{t('nav.login')}</Link>
          <Link to="/register" className="inline-flex items-center px-3.5 py-1.5 rounded-xl text-sm font-semibold text-white no-underline transition-all bg-white/22 border border-white/35 opacity-100 hover:bg-white/30">
            {t('nav.register')}
          </Link>
        </>
      )}
    </>
  );

  const drawerLinks = (
    <>
      <Link to="/" className={drawerLinkClass(isActive('/'))} onClick={() => setMenuOpen(false)}><Home size={18} /> {t('nav.home')}</Link>
      <Link to="/dashboard" className={drawerLinkClass(isActive('/dashboard'))} onClick={() => setMenuOpen(false)}><Map size={18} /> {t('nav.dashboard')}</Link>
      <Link to="/about" className={drawerLinkClass(isActive('/about'))} onClick={() => setMenuOpen(false)}><Info size={18} /> {t('nav.about')}</Link>
      {farmer ? (
        <>
          <Link to="/my-trees" className={drawerLinkClass(isActive('/my-trees'))} onClick={() => setMenuOpen(false)}><TreePine size={18} /> {t('nav.myTrees')}</Link>
          <Link to="/ndvi" className={drawerLinkClass(isActive('/ndvi'))} onClick={() => setMenuOpen(false)}><Activity size={18} /> {t('nav.ndvi')}</Link>
          <Link to="/eudr-compliance" className={drawerLinkClass(isActive('/eudr-compliance'))} onClick={() => setMenuOpen(false)}><FileText size={18} /> {t('nav.eudr')}</Link>
          <Link to="/carbon-farming" className={drawerLinkClass(isActive('/carbon-farming'))} onClick={() => setMenuOpen(false)}><Leaf size={18} /> {t('nav.crcf')}</Link>
          <Link to="/mrv" className={drawerLinkClass(isActive('/mrv'))} onClick={() => setMenuOpen(false)}><Activity size={18} /> MRV</Link>
          <Link to="/tree-labels" className={drawerLinkClass(isActive('/tree-labels'))} onClick={() => setMenuOpen(false)}><Printer size={18} /> {t('nav.labels')}</Link>
          <Link to="/profile" className={drawerLinkClass(isActive('/profile'))} onClick={() => setMenuOpen(false)}><User size={18} /> {t('nav.profile')}</Link>
          {farmer.is_admin && (
            <>
              <Link to="/admin" className={drawerLinkClass(isActive('/admin'))} onClick={() => setMenuOpen(false)}><Shield size={18} /> {t('nav.admin')}</Link>
              <Link to="/field-validation" className={drawerLinkClass(isActive('/field-validation'))} onClick={() => setMenuOpen(false)}><ClipboardCheck size={18} /> Field</Link>
            </>
          )}
          <div className="border-t border-white/15 my-2" />
          <span className="flex items-center px-4 py-2 text-sm text-white/70">{farmer.name}</span>
          <button onClick={() => { setMenuOpen(false); logout(); }} className="flex items-center gap-2 w-full px-4 py-3 rounded-xl text-base font-semibold text-white bg-white/8 border-none cursor-pointer text-left transition-all hover:bg-white/15 min-h-12">
            <LogOut size={18} /> {t('nav.logout')}
          </button>
        </>
      ) : (
        <>
          <Link to="/login" className={drawerLinkClass(isActive('/login'))} onClick={() => setMenuOpen(false)}>{t('nav.login')}</Link>
          <Link to="/register" className={drawerLinkClass(isActive('/register'))} onClick={() => setMenuOpen(false)}>{t('nav.register')}</Link>
        </>
      )}
    </>
  );

  return (
    <div className="flex flex-col min-h-screen font-sans">
      <a href="#main-content" className="skip-link">Skip to content</a>

      <header className={cn(
        'bg-primary text-white px-6 h-14 flex justify-between items-center sticky top-0 z-[100] transition-shadow duration-200',
        scrolled ? 'shadow-[0_2px_16px_rgba(0,0,0,0.2)]' : 'shadow-[0_1px_0_rgba(255,255,255,0.08)]'
      )}>
        <div className="flex items-center gap-1">
          <Link to="/" className="no-underline text-white flex items-center gap-2 mr-2">
            <TreePine size={20} className="opacity-90" />
            <span className="font-extrabold text-[1.1rem] tracking-tight">AvoClima</span>
          </Link>
          <nav className="hidden md:flex gap-0.5 items-center">
            {navLinks}
          </nav>
          <button
            className="flex md:hidden items-center justify-center bg-transparent border-none text-white cursor-pointer p-2 rounded-lg min-w-11 min-h-11"
            onClick={() => setMenuOpen((prev) => !prev)}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          >
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
        <div className="flex items-center gap-3">
          <span className="hidden md:block text-white/60 text-xs font-medium tracking-wide">
            Geospatial Tree Inventory & Monitoring
          </span>
          <LanguageSwitcher />
        </div>
      </header>

      {menuOpen && (
        <div className="fixed inset-0 bg-black/45 backdrop-blur-sm z-[200]" onClick={() => setMenuOpen(false)} />
      )}

      <div className={cn(
        'fixed top-0 right-0 bottom-0 w-70 max-w-[80vw] bg-primary z-[300] flex flex-col p-4 gap-0.5 overflow-y-auto transition-transform duration-250',
        menuOpen ? 'translate-x-0' : 'translate-x-full'
      )}>
        <div className="flex justify-between items-center mb-4 px-1">
          <div className="flex items-center gap-2 font-extrabold text-lg text-white">
            <TreePine size={18} /> AvoClima
          </div>
          <button onClick={() => setMenuOpen(false)} aria-label="Close menu" className="bg-transparent border-none text-white cursor-pointer p-2 rounded-lg min-w-11 min-h-11 flex items-center justify-center">
            <X size={22} />
          </button>
        </div>
        <nav className="flex flex-col gap-0.5">
          {drawerLinks}
        </nav>
      </div>

      <main id="main-content" className="flex-1">{children}</main>

      <footer className="bg-primary text-white/50 px-6 py-5 flex justify-between items-center flex-wrap gap-3 text-[0.78rem]">
        <div className="flex items-center gap-2 text-white/70 font-semibold">
          <TreePine size={14} />
          AvoClima — High-Integrity Monitoring for Agroforestry Carbon
        </div>
        <div className="flex gap-4">
          {['IPCC-compliant', 'EUDR-ready', 'CRCF-certified'].map(badge => (
            <span key={badge} className="bg-white/10 text-white/70 px-2.5 py-0.5 rounded-full text-[0.72rem] font-semibold">
              {badge}
            </span>
          ))}
        </div>
      </footer>

      <style>{`
        .skip-link { position: absolute; top: -100px; left: 0; background: #fff; color: #1b5e20; padding: 8px 16px; z-index: 200; font-weight: 600; font-size: 0.85rem; text-decoration: none; border-radius: 0 0 4px 0; transition: top 0.2s; }
        .skip-link:focus { top: 0; }
      `}</style>
    </div>
  );
}

export default Layout;
